import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSystem, SYSTEM_META } from "@/lib/system";
import { Panel, Badge } from "@/components/Bits";
import { Plus, Trash2, Edit3, Search, Building2, Upload, RefreshCw, Mail } from "lucide-react";
import { toast } from "sonner";

const ipt = "w-full bg-bg border border-line rounded-sm px-3 py-2 text-sm focus:border-cyan focus:outline-none focus:ring-1 focus:ring-cyan/40";

export default function Merchants() {
  const { user } = useAuth();
  const { system } = useSystem();
  const canManage = ["admin", "supervisor"].includes(user?.role);
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const { data } = await api.get("/merchants", { params: system ? { system } : {} });
    setList(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [system]);

  const filtered = useMemo(() =>
    list.filter((m) => !q || m.name.toLowerCase().includes(q.toLowerCase())),
    [list, q]
  );

  const total = filtered.reduce((s, m) => s + (m.ticket_count || 0), 0);
  const totalOpen = filtered.reduce((s, m) => s + (m.open_tickets || 0), 0);

  return (
    <div className="p-6 space-y-4 animate-slide-up" data-testid="merchants-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-cyan mb-1">Directory</div>
          <h1 className="text-2xl font-bold tracking-tight">Merchants &amp; Customers</h1>
          <p className="text-xs text-slate-400 mt-1">Auto-registered dari nama sheet Excel saat bulk import. Anda bisa tambah / edit / hapus manual.</p>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            <label data-testid="merchant-import-btn" className="text-xs uppercase tracking-wider border border-line text-slate-300 px-3 py-2 rounded-sm hover:bg-surface2 cursor-pointer flex items-center gap-1.5">
              <Upload className="w-3.5 h-3.5" /> Import Excel
              <input
                type="file" accept=".xlsx" className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  if (!confirm("REPLACE semua merchant dari Excel + remap semua tiket. Lanjut?")) {
                    e.target.value = ""; return;
                  }
                  const fd = new FormData();
                  fd.append("file", file); fd.append("replace", "true");
                  fd.append("dry_run", "false"); fd.append("remap_tickets", "true");
                  try {
                    const { data } = await api.post("/merchants/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`Imported ${data.created} merchant · remapped ${data.tickets_remapped} tickets · unmatched ${data.tickets_unmatched}`);
                    load();
                  } catch (e2) { toast.error(formatErr(e2)); }
                  finally { e.target.value = ""; }
                }}
              />
            </label>
            <button data-testid="merchant-remap-btn" onClick={async () => {
              if (!confirm("Re-link semua tiket ke merchant dari directory saat ini?")) return;
              try {
                const { data } = await api.post("/merchants/remap-tickets");
                toast.success(`Remapped ${data.remapped} tickets · ${data.cross_system_matches} cross-system · ${data.unmatched} unmatched`);
                load();
              } catch (e2) { toast.error(formatErr(e2)); }
            }} className="text-xs uppercase tracking-wider border border-line text-slate-300 px-3 py-2 rounded-sm hover:bg-surface2 flex items-center gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Remap Tickets
            </button>
            <button data-testid="new-merchant-btn" onClick={() => { setCreating(true); setEditing({ name: "", system: system || "TARAPAY", customer_type: "FOREX", default_payment_channel: "Ayolinx", notes: "", merchant_id: null, email: "" }); }} className="bg-cyan text-bg font-semibold px-4 py-2 rounded-sm text-sm flex items-center gap-2 hover:bg-cyan-dark">
              <Plus className="w-4 h-4" /> Add Merchant
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="bg-surface border border-line rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Merchants Shown</div>
          <div className="text-2xl font-mono font-bold text-white mt-1" data-testid="stat-merchant-count">{filtered.length}</div>
        </div>
        <div className="bg-surface border border-line rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Total Tickets</div>
          <div className="text-2xl font-mono font-bold text-cyan mt-1">{total}</div>
        </div>
        <div className="bg-surface border border-line rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Currently Open</div>
          <div className="text-2xl font-mono font-bold text-amber-400 mt-1">{totalOpen}</div>
        </div>
      </div>

      <div className="bg-surface border border-line rounded-sm p-3 relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-6 top-5" />
        <input data-testid="merchant-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari merchant..." className={`${ipt} pl-9`} />
      </div>

      <div className="bg-surface border border-line rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid="merchants-table">
          <thead className="bg-surface2 border-b border-line">
            <tr className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
              <th className="text-left px-3 py-2.5 font-medium">Merchant</th>
              <th className="text-left px-3 py-2.5 font-medium">ID</th>
              <th className="text-left px-3 py-2.5 font-medium">System</th>
              <th className="text-left px-3 py-2.5 font-medium">Email</th>
              <th className="text-left px-3 py-2.5 font-medium">Customer Type</th>
              <th className="text-right px-3 py-2.5 font-medium">Tickets</th>
              <th className="text-right px-3 py-2.5 font-medium">Open</th>
              <th className="px-3 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-slate-500" data-testid="empty-merchants">Tidak ada merchant. Import file Excel atau tambahkan manual.</td></tr>
            )}
            {filtered.map((m) => (
              <tr key={m.id} data-testid={`merchant-row-${m.id}`} className="border-b border-line/40 hover:bg-surface2">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-3.5 h-3.5 text-slate-500" />
                    <span className="font-medium">{m.name}</span>
                  </div>
                  {m.notes && <div className="text-[10px] text-slate-500 mt-0.5 ml-5 truncate max-w-md">{m.notes}</div>}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs text-slate-300">{m.merchant_id || <span className="text-slate-600">—</span>}</td>
                <td className="px-3 py-2.5"><Badge color={SYSTEM_META[m.system]?.color}>{SYSTEM_META[m.system]?.short}</Badge></td>
                <td className="px-3 py-2.5 text-xs text-slate-300">
                  {m.email ? (
                    <a href={`mailto:${m.email}`} className="hover:text-cyan flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {m.email}
                    </a>
                  ) : <span className="text-slate-600">—</span>}
                </td>
                <td className="px-3 py-2.5"><Badge color={m.customer_type === "GAMING" ? "awaiting" : "info"}>{m.customer_type}</Badge></td>
                <td className="px-3 py-2.5 text-right font-mono text-cyan">{m.ticket_count}</td>
                <td className="px-3 py-2.5 text-right font-mono text-amber-400">{m.open_tickets}</td>
                <td className="px-3 py-2.5">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`view-merchant-${m.id}`} onClick={() => nav(`/tickets?merchant=${encodeURIComponent(m.name)}`)} className="text-[10px] uppercase tracking-wider border border-line px-2 py-1 rounded-sm hover:bg-surface2 text-cyan">View tickets</button>
                    {canManage && (
                      <>
                        <button data-testid={`edit-merchant-${m.id}`} onClick={() => { setEditing(m); setCreating(false); }} className="text-[10px] border border-line px-2 py-1 rounded-sm hover:bg-surface2"><Edit3 className="w-3 h-3" /></button>
                        <button data-testid={`delete-merchant-${m.id}`} onClick={async () => { if (confirm(`Delete ${m.name}? (Tickets tidak terhapus)`)) { await api.delete(`/merchants/${m.id}`); load(); } }} className="text-[10px] border border-line px-2 py-1 rounded-sm hover:bg-rose-500/20 text-rose-400"><Trash2 className="w-3 h-3" /></button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <MerchantModal m={editing} creating={creating} onClose={() => { setEditing(null); setCreating(false); }} onSaved={() => { setEditing(null); setCreating(false); load(); }} />}
    </div>
  );
}

function MerchantModal({ m, creating, onClose, onSaved }) {
  const [f, setF] = useState(m);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      if (creating) await api.post("/merchants", f);
      else {
        const body = {
          name: f.name, system: f.system, customer_type: f.customer_type,
          default_payment_channel: f.default_payment_channel, notes: f.notes,
          merchant_id: f.merchant_id || null, email: f.email || "",
        };
        await api.patch(`/merchants/${f.id}`, body);
      }
      onSaved();
    } catch (e2) { setErr(formatErr(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="merchant-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-sm p-5 w-full max-w-md space-y-3">
        <h3 className="text-lg font-bold">{creating ? "Add Merchant" : "Edit Merchant"}</h3>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Name</label>
          <input data-testid="mm-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={ipt} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Merchant ID</label>
            <input data-testid="mm-merchant-id" type="number" value={f.merchant_id || ""} onChange={(e) => setF({ ...f, merchant_id: e.target.value ? parseInt(e.target.value, 10) : null })} className={`${ipt} font-mono`} placeholder="3000xxx" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Email</label>
            <input data-testid="mm-email" type="email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className={ipt} placeholder="cs@merchant.com" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">System</label>
            <select data-testid="mm-system" value={f.system} onChange={(e) => setF({ ...f, system: e.target.value })} className={ipt}>
              <option value="TARAPAY">Tarapay</option>
              <option value="SATURNUS">Saturnuspay</option>
            </select>
            {!creating && <div className="text-[10px] text-amber-400 mt-1">Mengubah system akan memindahkan semua tiket merchant ini</div>}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-400">Customer Type</label>
            <select data-testid="mm-customer-type" value={f.customer_type} onChange={(e) => setF({ ...f, customer_type: e.target.value })} className={ipt}>
              <option value="FOREX">Forex</option>
              <option value="GAMING">Gaming</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Default Payment Channel</label>
          <select data-testid="mm-channel" value={f.default_payment_channel || ""} onChange={(e) => setF({ ...f, default_payment_channel: e.target.value })} className={ipt}>
            <option value="">—</option>
            <option value="Ayolinx">Ayolinx</option>
            <option value="Pakailink">Pakailink</option>
            <option value="Pearpaygame">Pearpaygame</option>
            <option value="Easylink">Easylink</option>
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-slate-400">Notes</label>
          <textarea data-testid="mm-notes" rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} className={ipt} />
        </div>
        {err && <div className="text-xs text-rose-400">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border border-line px-4 py-2 text-sm rounded-sm hover:bg-surface2">Cancel</button>
          <button data-testid="mm-save" disabled={busy} className="bg-cyan text-bg font-semibold px-4 py-2 text-sm rounded-sm hover:bg-cyan-dark disabled:opacity-60">Save</button>
        </div>
      </form>
    </div>
  );
}
